import { useEffect } from 'react';
import { Control, ControllerRenderProps, FieldPath, FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Definition, getDefinitions, MaterialProperties, ProductType } from '@steelbuy/ts-shared';
import { FormItem, Icon, IconIdentifier } from '@steelbuy/ui-primitive';
import { CheckboxGroup } from '../CheckboxGroup';
import { Checkbox } from '../primitive/checkbox/Checkbox';

interface MaterialDefinitionSelectionProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
    material: MaterialProperties;
    name: TName;
    control: Control<TFieldValues>;
    error?: string;
    addedIndexes: (ProductType | Definition)[];
    showConfirmSelection: (onConfirm: () => void) => void;
    sortFn?: (a: Definition, b: Definition) => number;
}

export const MaterialDefinitionSelection = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    name,
    control,
    material,
    error,
    addedIndexes,
    showConfirmSelection,
    sortFn,
}: MaterialDefinitionSelectionProps<TFieldValues, TName>) => {
    const { t } = useTranslation(['domainModel', 'uiDomain']);
    const availableProductDefinitions = getDefinitions(material)
        ?.slice()
        .sort(sortFn ? (a, b) => sortFn(a as Definition, b as Definition) : undefined);
    const productOptions = availableProductDefinitions.map((materialDefinition) => ({
        label: t(`domainModel:material.definition.value.${materialDefinition}`),
        value: materialDefinition,
    }));

    const { field } = useController({ name, control });

    useEffect(() => {
        if (!availableProductDefinitions.length) {
            field.onChange(undefined);
        }
    }, [availableProductDefinitions.length]);

    if (!availableProductDefinitions.length) {
        return null;
    }

    const deselectDefinition = (definition: string, controllerField: ControllerRenderProps<TFieldValues, TName>) =>
        controllerField.onChange(controllerField.value.filter((item: string) => item !== definition));

    return (
        <FormItem
            header={t('domainModel:material.definition.name')}
            description={t('uiDomain:rfqForm.definition.description')}
            isMandatory
        >
            {!!error && <Icon name={IconIdentifier.ERROR} />}
            <CheckboxGroup>
                <Checkbox
                    control={control}
                    name={name}
                    options={productOptions}
                    shouldUnregister
                    rules={{
                        required: t('uiDomain:validation.requiredField'),
                    }}
                    onChange={({ target, field: controllerField }) => {
                        if (target.checked) {
                            controllerField.onChange([...controllerField.value, target.value]);
                            return;
                        }
                        if (addedIndexes.includes(target.value as Definition)) {
                            showConfirmSelection(() => deselectDefinition(target.value, controllerField));
                            return;
                        }
                        deselectDefinition(target.value, controllerField);
                    }}
                    id="definition-select"
                />
            </CheckboxGroup>
            {!!error && <span className="form-item__error-msg">{error}</span>}
        </FormItem>
    );
};
